import React, { useEffect, useState } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Phone, MapPin, User, CheckCircle, XCircle, Clock, Flame, Sparkles, AlertTriangle, RefreshCw, Loader2, Award, ChevronRight, Mail } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_META = {
  hot:      { label: 'Lead chaud',      color: '#DC2626', icon: Flame, bg: '#FEE2E2' },
  overdue:  { label: 'En retard',       color: '#F59E0B', icon: Clock, bg: '#FEF3C7' },
  new:      { label: 'Nouveau',         color: '#0EA5E9', icon: Sparkles, bg: '#E0F2FE' },
  extra:    { label: 'Lead du jour',    color: '#71717A', icon: Phone, bg: '#F4F4F5' },
};
const STATUS_META = {
  pending:  { label: 'À traiter', color: '#F97316', bg: '#FFF7ED' },
  done:     { label: 'Traité',    color: '#10B981', bg: '#ECFDF5' },
  overdue:  { label: 'En retard', color: '#DC2626', bg: '#FEF2F2' },
  skipped:  { label: 'Ignoré',    color: '#71717A', bg: '#F4F4F5' },
};
const RESULT_OPTIONS = [
  { value: 'appele',         label: 'Appelé' },
  { value: 'rdv_obtenu',     label: 'RDV obtenu' },
  { value: 'en_negociation', label: 'En négociation' },
  { value: 'rappel_demande', label: 'Rappel demandé' },
  { value: 'injoignable',    label: 'Injoignable' },
  { value: 'refus',          label: 'Refus' },
];

export default function OpportunitiesPage() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [board, setBoard] = useState(null);
  const [recent, setRecent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [doneOpp, setDoneOpp] = useState(null);
  const [doneForm, setDoneForm] = useState({ result_status: 'appele', comment: '' });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const userEmail = (user?.email || '').toLowerCase();

  const load = async () => {
    setLoading(true);
    try {
      const [t, d, r] = await Promise.all([
        api.get('/opportunities/today'),
        api.get('/opportunities/dashboard'),
        api.get('/opportunities/recent?days=14'),
      ]);
      setToday(t.data);
      setBoard(d.data);
      setRecent(r.data);
    } catch (e) {
      toast.error('Erreur chargement opportunités');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/opportunities/generate-now');
      toast.success(`Généré : ${data.generated} leads · ${data.emails_sent} emails envoyés`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setGenerating(false);
  };

  const canActOn = (opp) =>
    isAdmin || (opp.commercial_email || '').toLowerCase() === userEmail;

  const openDone = (opp) => {
    setDoneOpp(opp);
    setDoneForm({ result_status: 'appele', comment: '' });
  };
  const submitDone = async () => {
    try {
      await api.post(`/opportunities/${doneOpp.id}/done`, doneForm);
      toast.success('Opportunité traitée');
      setDoneOpp(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const skip = async (opp) => {
    const reason = window.prompt('Raison (optionnel) :') ?? '';
    try {
      await api.post(`/opportunities/${opp.id}/skip`, { reason });
      toast.success('Marqué comme ignoré');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Group today's items by commercial
  const todayByCommercial = React.useMemo(() => {
    if (!today?.items) return {};
    const m = {};
    for (const it of today.items) {
      if (!m[it.commercial_id]) m[it.commercial_id] = { meta: it, items: [] };
      m[it.commercial_id].items.push(it);
    }
    return m;
  }, [today]);

  // Backlog = pending + overdue across last 14 days, grouped by commercial
  const [filterCommercialId, setFilterCommercialId] = useState('all');
  const backlogGroups = React.useMemo(() => {
    if (!recent?.items) return [];
    const active = recent.items.filter(it =>
      it.status === 'pending' || it.status === 'overdue'
    );
    const m = {};
    for (const it of active) {
      if (!m[it.commercial_id]) {
        m[it.commercial_id] = {
          commercial_id: it.commercial_id,
          meta: it,
          pending: 0, overdue: 0, items: [],
        };
      }
      m[it.commercial_id][it.status] += 1;
      m[it.commercial_id].items.push(it);
    }
    let groups = Object.values(m).sort((a, b) => (b.overdue + b.pending) - (a.overdue + a.pending));
    if (filterCommercialId !== 'all') {
      groups = groups.filter(g => g.commercial_id === filterCommercialId);
    }
    return groups;
  }, [recent, filterCommercialId]);
  const backlogTotal = backlogGroups.reduce((s, g) => s + g.pending + g.overdue, 0);
  const backlogOverdue = backlogGroups.reduce((s, g) => s + g.overdue, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="opportunities-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#F97316] via-[#EA580C] to-[#1A1A1A] p-7 text-white shadow-lg">
        <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/70">Opportunités d'appels</p>
            <h1 className="text-3xl font-bold tracking-tight mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
              Tableau de mission
            </h1>
            <p className="text-sm text-white/80 mt-1">
              3 leads quotidiens par commercial · génération automatique du lundi au vendredi à 08h
            </p>
          </div>
          {isAdmin && (
            <Button onClick={generate} disabled={generating}
              className="bg-white text-[#1A1A1A] hover:bg-white/90 h-10 gap-2 font-semibold"
              data-testid="generate-now-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Générer maintenant
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard agrégé */}
      <Card className="border border-[#E5E7EB] shadow-sm" data-testid="opportunities-board-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-4 h-4 text-[#F97316]" />
            <h2 className="text-sm font-semibold">Performance commerciale (30 jours)</h2>
          </div>
          {loading ? (
            <p className="text-sm text-[#71717A]">Chargement...</p>
          ) : !board?.rows?.length ? (
            <p className="text-sm text-[#71717A]">Aucune opportunité encore générée.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {board.rows.map(r => (
                <div key={r.commercial_id} className="rounded-xl p-4 border border-[#E5E7EB] bg-white"
                  data-testid={`board-row-${r.commercial_id}`}>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: r.commercial_color || '#71717A' }}>
                      {(r.commercial_nom || '').split(' ').map(s => s[0]).join('').slice(0, 2)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{r.commercial_nom}</p>
                      <p className="text-[10px] text-[#71717A]">{r.commercial_email}</p>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between mt-2">
                    <span className="text-2xl font-bold tabular-nums">{r.completion_rate}%</span>
                    <span className="text-[10px] text-[#71717A]">{r.done}/{r.total} traités</span>
                  </div>
                  <div className="h-1.5 mt-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${r.completion_rate}%`, background: r.commercial_color }} />
                  </div>
                  <div className="mt-2 flex gap-2 text-[10px]">
                    <span className="px-1.5 py-0.5 rounded bg-[#FEF2F2] text-[#DC2626]">⏰ {r.overdue}</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#FFF7ED] text-[#EA580C]">🟠 {r.pending}</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#ECFDF5] text-[#10B981]">✓ {r.done}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backlog actif (à appeler + en retard) — visible à tous */}
      <Card className="border border-[#E5E7EB] shadow-sm" data-testid="backlog-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#DC2626]" />
              <h2 className="text-sm font-semibold">Backlog actif — à appeler ou rappeler</h2>
              <span className="text-xs text-[#71717A]">
                {backlogTotal} ouvert(s) · <span className="text-[#DC2626] font-semibold">{backlogOverdue} en retard</span>
              </span>
            </div>
            {isAdmin && board?.rows?.length > 0 && (
              <Select value={filterCommercialId} onValueChange={setFilterCommercialId}>
                <SelectTrigger className="h-8 w-56 text-xs bg-white" data-testid="backlog-filter">
                  <SelectValue placeholder="Filtrer par commercial" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les commerciaux</SelectItem>
                  {board.rows.map(r => (
                    <SelectItem key={r.commercial_id} value={r.commercial_id}>
                      {r.commercial_nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          {backlogGroups.length === 0 ? (
            <div className="text-sm text-[#71717A] text-center py-8">
              <CheckCircle className="w-8 h-8 text-[#10B981] mx-auto mb-2" />
              Aucun lead en attente ou en retard. Bien joué !
            </div>
          ) : (
            <div className="space-y-3">
              {backlogGroups.map(g => (
                <details key={g.commercial_id} open={isAdmin || g.commercial_email?.toLowerCase() === userEmail}
                  className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white"
                  data-testid={`backlog-group-${g.commercial_id}`}>
                  <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAFA]"
                    style={{ background: (g.meta.commercial_color || '#71717A') + '0a' }}>
                    <div className="w-8 h-8 rounded-md flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                      style={{ background: g.meta.commercial_color || '#71717A' }}>
                      {(g.meta.commercial_nom || '').split(' ').map(s => s[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{g.meta.commercial_nom}</p>
                      <p className="text-[10px] text-[#71717A] truncate">{g.meta.commercial_email}</p>
                    </div>
                    <div className="flex gap-1.5 text-[11px] flex-shrink-0">
                      <span className="px-2 py-0.5 rounded-full bg-[#FEF2F2] text-[#DC2626] font-semibold">⏰ {g.overdue}</span>
                      <span className="px-2 py-0.5 rounded-full bg-[#FFF7ED] text-[#EA580C] font-semibold">📞 {g.pending}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#71717A] flex-shrink-0" />
                  </summary>
                  <div className="divide-y divide-[#F3F4F6]">
                    {g.items.slice(0, 25).map(opp => {
                      const cat = CATEGORY_META[opp.category] || CATEGORY_META.extra;
                      const st = STATUS_META[opp.status];
                      const ps = opp.prospect_snapshot || {};
                      const acts = canActOn(opp);
                      return (
                        <div key={opp.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-[#FAFAFA]"
                          data-testid={`backlog-item-${opp.id}`}>
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold flex-shrink-0"
                            style={{ background: st.bg, color: st.color }}>
                            {st.label}
                          </span>
                          <span className="text-[10px] tabular-nums text-[#71717A] flex-shrink-0">{opp.date}</span>
                          <Link to={`/prospects?prospect=${opp.prospect_id}`}
                            className="text-xs font-medium truncate hover:text-[#F97316] flex-1">
                            {ps.enseigne} {ps.nom_magasin}
                            <span className="text-[#71717A] font-normal"> · {ps.ville}</span>
                            {(ps.prenom_dirigeant || ps.nom_dirigeant) && (
                              <span className="text-[#52525B]"> · {ps.prenom_dirigeant} {ps.nom_dirigeant}</span>
                            )}
                          </Link>
                          {ps.telephone && (
                            <span className="text-[11px] text-[#71717A] tabular-nums hidden md:inline">{ps.telephone}</span>
                          )}
                          {acts ? (
                            <Button size="sm" onClick={() => openDone(opp)}
                              className="h-6 text-[10px] px-2 bg-[#10B981] hover:bg-[#059669] text-white flex-shrink-0"
                              data-testid={`backlog-done-${opp.id}`}>
                              Marquer fait
                            </Button>
                          ) : (
                            <span className="text-[10px] text-[#A1A1AA] italic">Lecture</span>
                          )}
                        </div>
                      );
                    })}
                    {g.items.length > 25 && (
                      <div className="px-4 py-2 text-[10px] text-center text-[#71717A]">
                        + {g.items.length - 25} autres leads…
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aujourd'hui par commercial */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#F97316]" /> Aujourd'hui ({today?.count || 0} leads)
          </h2>
          <span className="text-xs text-[#71717A] tabular-nums">{today?.date}</span>
        </div>
        {!today || today.count === 0 ? (
          <Card className="border border-dashed border-[#E5E7EB] shadow-none">
            <CardContent className="p-12 text-center">
              <Sparkles className="w-10 h-10 text-[#CBD5E1] mx-auto mb-2" />
              <p className="text-sm text-[#71717A]">Aucune opportunité générée aujourd'hui.</p>
              {isAdmin && (
                <Button onClick={generate} disabled={generating}
                  className="mt-4 bg-[#F97316] hover:bg-[#EA580C] text-white gap-2">
                  <RefreshCw className="w-4 h-4" /> Générer maintenant
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {Object.values(todayByCommercial).map(group => (
              <Card key={group.meta.commercial_id}
                className="border border-[#E5E7EB] shadow-sm overflow-hidden"
                data-testid={`today-group-${group.meta.commercial_id}`}>
                <div className="px-5 py-3 flex items-center justify-between"
                  style={{ background: (group.meta.commercial_color || '#71717A') + '15' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: group.meta.commercial_color || '#71717A' }}>
                      {(group.meta.commercial_nom || '').split(' ').map(s => s[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{group.meta.commercial_nom}</p>
                      <p className="text-[10px] text-[#71717A]">{group.meta.commercial_email}</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {group.items.filter(i => i.status === 'done').length}/{group.items.length} traités
                  </Badge>
                </div>
                <div className="divide-y divide-[#E5E7EB]">
                  {group.items.map(opp => {
                    const cat = CATEGORY_META[opp.category] || CATEGORY_META.extra;
                    const st = STATUS_META[opp.status];
                    const Icon = cat.icon;
                    const acts = canActOn(opp);
                    const ps = opp.prospect_snapshot || {};
                    return (
                      <div key={opp.id} className="px-5 py-3 flex items-start gap-3 hover:bg-[#FAFAFA]"
                        data-testid={`opp-row-${opp.id}`}>
                        <div className="rounded-lg p-2 flex-shrink-0" style={{ background: cat.bg }}>
                          <Icon className="w-4 h-4" style={{ color: cat.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link to={`/prospects?prospect=${opp.prospect_id}`}
                              className="text-sm font-semibold truncate hover:text-[#F97316]">
                              {ps.enseigne} {ps.nom_magasin}
                            </Link>
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{ background: cat.bg, color: cat.color }}>{cat.label}</span>
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{ background: st.bg, color: st.color }}>{st.label}</span>
                          </div>
                          <p className="text-xs text-[#71717A] mt-0.5">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {ps.rue} · {ps.code_postal} {ps.ville}
                          </p>
                          <p className="text-xs text-[#27272A] mt-0.5">
                            <User className="w-3 h-3 inline mr-1" />
                            <strong>{ps.prenom_dirigeant} {ps.nom_dirigeant || '(dirigeant non renseigné)'}</strong>
                            {ps.telephone && <> · <Phone className="w-3 h-3 inline mx-1" />{ps.telephone}</>}
                          </p>
                          {opp.status === 'done' && (
                            <p className="text-[11px] text-[#10B981] mt-1">
                              ✓ {RESULT_OPTIONS.find(o => o.value === opp.result_status)?.label || opp.result_status}
                              {opp.done_by_name && ` · par ${opp.done_by_name}`}
                              {opp.comment && <em className="text-[#71717A]"> — "{opp.comment}"</em>}
                            </p>
                          )}
                        </div>
                        {opp.status === 'pending' || opp.status === 'overdue' ? (
                          <div className="flex gap-1 flex-shrink-0">
                            {acts ? (
                              <>
                                <Button size="sm" variant="outline" onClick={() => skip(opp)}
                                  className="h-7 text-[11px]" data-testid={`skip-${opp.id}`}>
                                  <XCircle className="w-3 h-3 mr-1" />Ignorer
                                </Button>
                                <Button size="sm" onClick={() => openDone(opp)}
                                  className="h-7 text-[11px] bg-[#10B981] hover:bg-[#059669] text-white"
                                  data-testid={`done-${opp.id}`}>
                                  <CheckCircle className="w-3 h-3 mr-1" />Marquer fait
                                </Button>
                              </>
                            ) : (
                              <span className="text-[10px] text-[#A1A1AA] italic px-2 py-1">Lecture seule</span>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Mark done dialog */}
      <Dialog open={!!doneOpp} onOpenChange={(o) => !o && setDoneOpp(null)}>
        <DialogContent data-testid="done-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Résultat de l'appel</DialogTitle>
          </DialogHeader>
          {doneOpp && (
            <div className="space-y-3 pt-2">
              <p className="text-sm text-[#71717A]">
                {doneOpp.prospect_snapshot?.enseigne} {doneOpp.prospect_snapshot?.nom_magasin}
              </p>
              <div>
                <Label className="text-sm mb-1 block">Statut</Label>
                <Select value={doneForm.result_status} onValueChange={(v) => setDoneForm(f => ({ ...f, result_status: v }))}>
                  <SelectTrigger className="h-10 bg-white" data-testid="done-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RESULT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm mb-1 block">Commentaire</Label>
                <Textarea rows={3} value={doneForm.comment}
                  onChange={(e) => setDoneForm(f => ({ ...f, comment: e.target.value }))}
                  placeholder="Note libre — sera ajoutée à la timeline du prospect."
                  className="bg-white" data-testid="done-comment-input" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setDoneOpp(null)} className="flex-1 h-10">Annuler</Button>
                <Button onClick={submitDone}
                  className="flex-1 h-10 bg-[#10B981] hover:bg-[#059669] text-white"
                  data-testid="submit-done-btn">
                  <CheckCircle className="w-4 h-4 mr-2" />Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
